import type {
  AssetsApiTypeDto,
  AssetsQueryDto,
  AssetsSearchBodyDto,
  AssetsSearchGroupBy,
  AssetsSearchMetricsBodyDto,
  AssetsSearchOrderBy,
} from '@terra/shared-contracts-assets';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';
import { endOfDay, startOfDay } from 'date-fns';
import { resolveCreatedStartBound } from '../../utils/created-since';
import {
  ASSET_TYPE,
  supportsCodeGraphFilter,
  type AssetType,
  type AssetsDetectionView,
  type AssetsSecurityFilter,
  type HttpMethodVerb,
} from '../../store/tab/assetsTabScopedFilterStore';

type PreviewOrderByField = NonNullable<
  AssetsQueryDto['orderBy']
>[number]['field'];

const PREVIEW_ORDER_BY_FIELDS: readonly PreviewOrderByField[] = [
  'updatedAt',
  'createdAt',
  'url',
];

export type AssetsSearchRequestFilters = {
  typeFilter: AssetType;
  methodsFilter: HttpMethodVerb[];
  apiTypes?: AssetsApiTypeDto[];
  searchTerm: string;
  securityFilter?: AssetsSecurityFilter | null;
  detectionView?: AssetsDetectionView;
  createdStartDate?: Date | null;
  createdEndDate?: Date | null;
  createdSince?: Date | null;
  updatedStartDate?: Date | null;
  updatedEndDate?: Date | null;
  withCodeGraph: boolean;
  orderBy?: AssetsSearchOrderBy[];
  groupBy?: AssetsSearchGroupBy;
  domainFilters?: AssetsQueryDto['domainFilters'];
  lifecycleState?: LifecycleStateFilterDto;
};

type AssetsSearchType = AssetsSearchBodyDto['type'];

const toAssetsSearchType = (typeFilter: AssetType): AssetsSearchType | null => {
  switch (typeFilter) {
    case ASSET_TYPE.ENDPOINTS:
      return 'allEndpoints';
    case ASSET_TYPE.WEB_ENDPOINTS:
      return 'endpoints';
    case ASSET_TYPE.WEB_PAGES:
      return 'webpages';
    case ASSET_TYPE.AGENTS:
      return 'agents';
    case ASSET_TYPE.ALL:
      return null;
  }
};

export const toAssetsQueryType = (
  typeFilter: AssetType,
): NonNullable<AssetsQueryDto['type']> =>
  typeFilter === ASSET_TYPE.WEB_ENDPOINTS ? ASSET_TYPE.ENDPOINTS : typeFilter;

export const supportsAssetsSearchEndpoint = (typeFilter: AssetType): boolean =>
  toAssetsSearchType(typeFilter) !== null;

const isPreviewOrderByField = (
  field: AssetsSearchOrderBy['field'],
): field is PreviewOrderByField =>
  PREVIEW_ORDER_BY_FIELDS.some((previewField) => previewField === field);

export const toPreviewOrderBy = (
  orderBy: AssetsSearchOrderBy[] | undefined,
): AssetsQueryDto['orderBy'] => {
  const previewOrderBy = (orderBy ?? []).flatMap((entry) => {
    const { field } = entry;

    return isPreviewOrderByField(field) ? [{ field, order: entry.order }] : [];
  });

  return previewOrderBy.length > 0 ? previewOrderBy : undefined;
};

type AssetsDateRangeFilters = Pick<
  AssetsSearchRequestFilters,
  | 'createdStartDate'
  | 'createdEndDate'
  | 'createdSince'
  | 'updatedStartDate'
  | 'updatedEndDate'
>;

type AssetsDateRangeParams = Pick<
  AssetsSearchBodyDto,
  | 'createdStartDate'
  | 'createdEndDate'
  | 'updatedStartDate'
  | 'updatedEndDate'
  | 'dateRangeBoundary'
>;

export const toLocalDayDateRangeParams = ({
  createdStartDate,
  createdEndDate,
  createdSince,
  updatedStartDate,
  updatedEndDate,
}: AssetsDateRangeFilters): AssetsDateRangeParams => {
  const createdStartBound = resolveCreatedStartBound({
    createdStartDate: createdStartDate ? startOfDay(createdStartDate) : null,
    createdSince: createdSince ?? null,
  });
  const params: AssetsDateRangeParams = {
    ...(createdStartBound ? { createdStartDate: createdStartBound } : {}),
    ...(createdEndDate ? { createdEndDate: endOfDay(createdEndDate) } : {}),
    ...(updatedStartDate
      ? { updatedStartDate: startOfDay(updatedStartDate) }
      : {}),
    ...(updatedEndDate ? { updatedEndDate: endOfDay(updatedEndDate) } : {}),
  };

  return Object.keys(params).length > 0
    ? { ...params, dateRangeBoundary: 'exact' }
    : params;
};

const buildVerifiedAssetsFilterFields = ({
  filters,
  isEndpointList,
}: {
  filters: AssetsSearchRequestFilters;
  isEndpointList: boolean;
}): Omit<AssetsSearchMetricsBodyDto, 'type'> => {
  const trimmedSearchTerm = filters.searchTerm.trim();

  return {
    roleFilterType: 'multiple',
    detectionView: filters.detectionView ?? 'active',
    ...(trimmedSearchTerm ? { url: trimmedSearchTerm } : {}),
    ...(isEndpointList && filters.methodsFilter.length > 0
      ? { methods: [...filters.methodsFilter] }
      : {}),
    ...(isEndpointList && filters.apiTypes && filters.apiTypes.length > 0
      ? { apiTypes: filters.apiTypes }
      : {}),
    ...(filters.domainFilters ? { domainFilters: filters.domainFilters } : {}),
    ...toLocalDayDateRangeParams(filters),
    ...(filters.withCodeGraph && supportsCodeGraphFilter(filters.typeFilter)
      ? { withCodeGraph: true }
      : {}),
    ...(filters.lifecycleState
      ? { lifecycleState: filters.lifecycleState }
      : {}),
  };
};

export const buildVerifiedAssetsSearchBody = ({
  filters,
  skip,
  limit,
}: {
  filters: AssetsSearchRequestFilters;
  skip: number;
  limit: number;
}): AssetsSearchBodyDto | null => {
  const type = toAssetsSearchType(filters.typeFilter);

  if (!type) {
    return null;
  }

  const isEndpointList = type !== 'webpages';
  const groupBy =
    filters.groupBy === 'method' && !isEndpointList
      ? undefined
      : filters.groupBy;

  return {
    type,
    ...buildVerifiedAssetsFilterFields({ filters, isEndpointList }),
    skip,
    limit,
    ...(isEndpointList && filters.securityFilter
      ? { endpointSecurityType: filters.securityFilter }
      : {}),
    ...(groupBy ? { groupBy } : {}),
    ...(filters.orderBy && filters.orderBy.length > 0
      ? { orderBy: filters.orderBy }
      : {}),
  };
};

export const buildVerifiedAssetsMetricsBody = ({
  filters,
  type,
}: {
  filters: AssetsSearchRequestFilters;
  type: Extract<AssetsSearchType, 'allEndpoints' | 'webpages'>;
}): AssetsSearchMetricsBodyDto => ({
  type,
  ...buildVerifiedAssetsFilterFields({
    filters,
    isEndpointList: type !== 'webpages',
  }),
});
